import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import Scout from './Scout';
import { AG } from './data';

// Warehouse tab of the TBS demo — the one view backed by live data
// (/api/warehouse): every branch's shelf ledger, Wira's replenishment runs
// from the two DCs, and the shipments moving out to branches.

const MONO = "'Geist Mono',monospace";
const STEPS = ['PREPARING', 'PACKED', 'IN_TRANSIT', 'DELIVERED', 'RECONCILED'];
const STEP_LABEL = { PREPARING: 'Preparing', PACKED: 'Packed', IN_TRANSIT: 'In transit', DELIVERED: 'Delivered', RECONCILED: 'Reconciled' };
const STEP_COLOR = {
  PREPARING: ['#16161B', '#C4C4CC'], PACKED: ['#1E1733', '#C4B5FD'], IN_TRANSIT: ['#2A200C', '#F7C66A'],
  DELIVERED: ['#0C2A1C', '#5BE3A0'], RECONCILED: ['#16161B', '#9C9CA6'],
};
const n = (x) => Math.round(Number(x) || 0).toLocaleString('id-ID');
const fillColor = (pct) => (pct < 45 ? '#FF6B6B' : pct < 65 ? '#F7C66A' : '#5BE3A0');

const card = { background: '#0A0A0D', border: '1px solid #1C1C22', borderRadius: 14 };
const eyebrow = { font: `500 10px ${MONO}`, letterSpacing: '.16em', color: '#6A6A74', textTransform: 'uppercase' };
const btn = (primary) => ({
  height: 34, padding: '0 14px', borderRadius: 9, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap',
  border: primary ? 0 : '1px solid #26262E', background: primary ? '#2F6BFF' : '#0E0E12', color: primary ? '#fff' : '#F2F2F4',
});

export default function WarehouseView({ onEnter }) {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [skus, setSkus] = useState([]);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null);
  const [error, setError] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const get = useCallback((path) => axios.get(`${API}/warehouse${path}`, { withCredentials: true }).then((r) => r.data), [API]);
  const post = useCallback((path) => axios.post(`${API}${path}`, {}, { withCredentials: true }).then((r) => r.data), [API]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setData(await get('/state'));  // one request: overview, branches, shipments, DC stock
      setError('');
    } catch (e) {
      setError('Couldn’t load warehouse data. Try again in a moment.');
    } finally {
      setRefreshing(false);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);
  // The demo-script panel floats over the left of the page; tuck it away here
  // (Scout's bubble stays, one click reopens it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onEnter?.(); }, []);

  const selected = storeId || data?.branches?.[0]?.id;
  useEffect(() => {
    if (!selected) return;
    get(`/branch-stock?store_id=${selected}`).then(setSkus).catch(() => setSkus([]));
  }, [selected, get, data]);

  const act = async (key, fn, describe) => {
    setBusy(key);
    setConfirmReset(false);
    let ok = false;
    try {
      setNote(describe(await fn()));
      ok = true;
    } catch (e) {
      setError('That didn’t go through. Try again in a moment.');
    } finally {
      // Unlock as soon as the action lands; the numbers refresh right behind it.
      setBusy('');
    }
    if (ok) load();
  };

  const runReplenishment = () => act('run', () => post('/warehouse/runs'), (r) => {
    const short = r.shortfalls.map((s) => `${s.dc} is short ${n(s.missing)} ${s.product}, flagged to Tara for a supplier PO`);
    return {
      agent: 'wira',
      text: r.shipments.length
        ? `Checked ${n(r.rows_checked)} shelves. ${r.below_reorder} were at or below reorder, so I planned ${r.shipments.length} shipments (${n(r.units)} units) from the DCs.`
        : `Checked ${n(r.rows_checked)} shelves. Every branch is above reorder or already has stock on the way. Nothing to ship.`,
      extra: short,
    };
  });
  const simulateDay = () => act('day', () => post('/warehouse/simulate-day'), (r) => ({
    agent: 'sari', text: `A day of trading across 14 branches: ${n(r.units_sold)} units sold off the shelves.`, extra: [],
  }));
  const advanceAll = () => act('all', () => post('/warehouse/shipments/advance-all'), (r) => ({
    agent: 'wira', text: `Moved ${r.advanced} shipments one step forward. Stock lands on a branch's shelf when its shipment is delivered.`, extra: [],
  }));
  const advance = (s) => act(s.id, () => post(`/warehouse/shipments/${s.id}/advance`), (r) => ({
    agent: 'wira',
    text: r.status === 'DELIVERED'
      ? `${s.store_name} received ${n(s.units)} units from ${s.dc_name}. Shelf stock updated.`
      : `${s.store_name} shipment is now ${STEP_LABEL[r.status].toLowerCase()}.`,
    extra: [],
  }));
  const reset = () => {
    if (!confirmReset) { setConfirmReset(true); return; }
    act('reset', () => post('/procurement/seed'), () => ({ agent: 'scout', text: 'Demo data reset: 14 branches, both DCs, fresh shelves.', extra: [] }));
    setStoreId(null);
  };

  const vm = useMemo(() => {
    if (!data) return null;
    const o = data.overview;
    const kpis = [
      { label: 'Network shelf fill', value: `${o.fill_pct}%`, sub: 'on hand vs par, all branches', color: fillColor(o.fill_pct) },
      { label: 'Branches needing stock', value: `${o.branches_below}/${o.branches}`, sub: `${o.below_reorder} SKUs at or below reorder`, color: o.branches_below ? '#F7C66A' : '#5BE3A0' },
      { label: 'Shipments in flight', value: o.open_shipments, sub: `${n(o.units_in_flight)} units on the way`, color: '#F2F2F4' },
      { label: 'DC shortfalls', value: o.open_shortfalls, sub: o.open_shortfalls ? 'waiting on Tara · procurement' : 'DCs covering every branch', color: o.open_shortfalls ? '#FF8A5B' : '#5BE3A0' },
      { label: 'Last run', value: o.last_run ? `${o.last_run.shipments_created} ships` : '—', sub: o.last_run ? `${n(o.last_run.units_planned)} units planned` : 'no replenishment run yet', color: '#6F97FF' },
    ];
    const dcs = {};
    data.dcStock.forEach((r) => { (dcs[r.dc_code] = dcs[r.dc_code] || { name: r.dc_name, rows: [] }).rows.push(r); });
    const open = data.shipments.filter((s) => s.status !== 'RECONCILED');
    const done = data.shipments.filter((s) => s.status === 'RECONCILED');
    return { kpis, dcs: Object.values(dcs), open, doneCount: done.length };
  }, [data]);

  const branch = data?.branches?.find((b) => b.id === selected);

  if (!vm) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: '#050506' }}>
        {error ? <div style={{ color: '#9C9CA6', fontSize: 13 }}>{error}</div>
          : <div style={{ width: 28, height: 28, border: '2px solid #26262E', borderTopColor: '#2F6BFF', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />}
      </div>
    );
  }

  // Left gutter clears the floating Scout button (bottom-left, ~96px wide).
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px 140px 120px', background: 'radial-gradient(1200px 520px at 72% -5%, #1A1433 0%, #050506 62%)' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, font: `500 10.5px ${MONO}`, letterSpacing: '.2em', border: '1px solid #33285A', color: '#C4B5FD', padding: '5px 11px', borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#A78BFA' }} />WIRA · LIVE DATA
              </span>
              <span style={{ ...eyebrow, letterSpacing: '.16em' }}>DC PULOGADUNG · DC SERPONG → 14 BRANCHES</span>
            </div>
            <div style={{ font: "400 48px/1.04 'Instrument Serif',serif", letterSpacing: '-.01em' }}>Every branch, topped up from the DC.</div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: '#9C9CA6', maxWidth: 700 }}>
              Wira watches every branch’s shelf for each SKU. When one drops to its reorder point, Wira ships it back to par from its DC, one truck per branch. If a DC runs short, Tara gets a shortfall to buy against.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flex: 'none' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btn(false)} onClick={simulateDay} disabled={!!busy}>{busy === 'day' ? 'Selling…' : 'Simulate a day of sales'}</button>
              <button style={btn(true)} onClick={runReplenishment} disabled={!!busy}>{busy === 'run' ? 'Wira is planning…' : '▶ Run replenishment'}</button>
            </div>
            <button onClick={reset} disabled={!!busy} style={{ border: 0, background: 'transparent', color: confirmReset ? '#FF8A5B' : '#6A6A74', font: `500 10px ${MONO}`, letterSpacing: '.12em', cursor: 'pointer', padding: 0 }}>
              {busy === 'reset' ? 'RESETTING…' : confirmReset ? 'CLICK AGAIN TO RESET ALL DEMO DATA' : 'RESET DEMO DATA'}
            </button>
          </div>
        </div>

        {/* Agent note */}
        {note && (
          <div style={{ ...card, display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 16px', borderColor: AG[note.agent]?.line || '#1C2A55', background: AG[note.agent]?.soft || '#0B1328' }}>
            <div style={{ width: 38, height: 38, flex: 'none' }}><Scout agent={note.agent} head /></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <span style={{ font: `500 10px ${MONO}`, letterSpacing: '.16em', color: AG[note.agent]?.color || '#8AAEFF' }}>{(AG[note.agent]?.name || 'Scout').toUpperCase()}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.55 }}>{note.text}</span>
              {note.extra.map((x) => <span key={x} style={{ fontSize: 13, color: '#FFA784' }}>⚠ {x}.</span>)}
            </div>
          </div>
        )}
        {error && <div style={{ fontSize: 13, color: '#FF8A5B' }}>{error}</div>}

        {/* KPIs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: -12, height: 14 }}>
          <span style={eyebrow}>Live numbers</span>
          {refreshing && <span style={{ ...eyebrow, color: '#8AAEFF' }}>· updating…</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12, opacity: refreshing ? 0.6 : 1, transition: 'opacity .2s' }}>
          {vm.kpis.map((k) => (
            <div key={k.label} style={{ ...card, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={eyebrow}>{k.label}</span>
              <span style={{ font: "400 34px/1 'Instrument Serif',serif", color: k.color }}>{k.value}</span>
              <span style={{ fontSize: 12, color: '#6A6A74' }}>{k.sub}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,7fr) minmax(0,5fr)', gap: 16, alignItems: 'start' }}>
          {/* Branches */}
          <div style={{ ...card, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={eyebrow}>Branches · shelf fill vs par</span>
              <span style={{ fontSize: 12, color: '#6A6A74' }}>Click a branch for its SKUs</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
              {data.branches.map((b) => {
                const active = b.id === selected;
                return (
                  <button key={b.id} onClick={() => setStoreId(b.id)} style={{ textAlign: 'left', cursor: 'pointer', padding: '10px 12px', borderRadius: 10, border: `1px solid ${active ? '#2F6BFF' : '#1C1C22'}`, background: active ? '#0B1328' : '#0E0E12', color: '#F2F2F4', display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <span style={{ font: `500 9.5px ${MONO}`, letterSpacing: '.1em', color: '#9C9CA6', border: '1px solid #26262E', borderRadius: 5, padding: '2px 5px', flex: 'none' }}>{b.dc_code}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 5, borderRadius: 3, background: '#1C1C22', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, b.fill_pct)}%`, height: '100%', background: fillColor(b.fill_pct), transition: 'width .5s' }} />
                      </div>
                      <span style={{ font: `500 11px ${MONO}`, color: '#9C9CA6', width: 40, textAlign: 'right', flex: 'none' }}>{Math.round(b.fill_pct)}%</span>
                      <span style={{ fontSize: 11.5, color: b.low ? '#F7C66A' : '#5BE3A0', width: 52, textAlign: 'right', flex: 'none', whiteSpace: 'nowrap' }}>{b.low ? `${b.low} low` : 'healthy'}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {branch && (
              <div style={{ borderTop: '1px solid #1C1C22', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ font: "400 22px/1.1 'Instrument Serif',serif" }}>{branch.name}</span>
                  <span style={{ fontSize: 12, color: '#6A6A74' }}>served by {branch.dc_name}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) repeat(4, minmax(0,.7fr)) minmax(0,.9fr)', gap: 0, fontSize: 12.5 }}>
                  {['SKU', 'On hand', 'Reorder', 'Par', 'Incoming', 'Status'].map((h) => (
                    <span key={h} style={{ ...eyebrow, fontSize: 9.5, padding: '6px 4px', borderBottom: '1px solid #1C1C22', textAlign: h === 'SKU' || h === 'Status' ? 'left' : 'right' }}>{h}</span>
                  ))}
                  {skus.map((r) => {
                    const low = r.qty_on_hand <= r.reorder_point;
                    const status = low ? (r.incoming > 0 ? ['On the way', '#F7C66A'] : ['Below reorder', '#FF6B6B']) : ['OK', '#5BE3A0'];
                    const cell = { padding: '7px 4px', borderBottom: '1px solid #121216', textAlign: 'right', fontFamily: MONO, fontSize: 12 };
                    return (
                      <React.Fragment key={r.id}>
                        <span style={{ ...cell, textAlign: 'left', fontFamily: 'inherit', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product_name}</span>
                        <span style={{ ...cell, color: low ? '#FF8A8A' : '#F2F2F4' }}>{n(r.qty_on_hand)}</span>
                        <span style={{ ...cell, color: '#6A6A74' }}>{n(r.reorder_point)}</span>
                        <span style={{ ...cell, color: '#6A6A74' }}>{n(r.par_level)}</span>
                        <span style={{ ...cell, color: r.incoming > 0 ? '#C4B5FD' : '#3A3A44' }}>{r.incoming > 0 ? `+${n(r.incoming)}` : '—'}</span>
                        <span style={{ ...cell, textAlign: 'left', fontFamily: 'inherit', fontSize: 12, color: status[1], whiteSpace: 'nowrap' }}>{status[0]}</span>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Shipments */}
          <div style={{ ...card, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...eyebrow, flex: 1 }}>Shipments · DC → branch</span>
              {vm.open.length > 0 && (
                <button onClick={advanceAll} disabled={!!busy} style={{ ...btn(false), height: 28, fontSize: 11.5, padding: '0 10px' }}>{busy === 'all' ? 'Moving…' : 'Advance all'}</button>
              )}
            </div>
            {vm.open.length === 0 && (
              <div style={{ padding: '22px 6px', textAlign: 'center', fontSize: 13, lineHeight: 1.6, color: '#6A6A74' }}>
                No shipments on the way.<br />Run replenishment to have Wira plan today’s trucks.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 640, overflow: 'auto' }}>
              {vm.open.map((s) => {
                const idx = STEPS.indexOf(s.status);
                const [bg, fg] = STEP_COLOR[s.status];
                return (
                  <div key={s.id} style={{ border: '1px solid #1C1C22', background: '#0E0E12', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.store_name}</span>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: bg, color: fg, whiteSpace: 'nowrap', flex: 'none' }}>{STEP_LABEL[s.status]}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6A6A74', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.dc_code} · {n(s.units)} units · {s.lines.map((l) => l.product_name).join(', ')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, display: 'flex', gap: 3 }}>
                        {STEPS.map((st, i) => <div key={st} title={STEP_LABEL[st]} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= idx ? '#A78BFA' : '#1C1C22' }} />)}
                      </div>
                      <button onClick={() => advance(s)} disabled={!!busy} style={{ ...btn(false), height: 26, fontSize: 11, padding: '0 9px', flex: 'none' }}>
                        {busy === s.id ? '…' : `→ ${STEP_LABEL[STEPS[idx + 1]]}`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {vm.doneCount > 0 && <span style={{ fontSize: 12, color: '#6A6A74' }}>{vm.doneCount} shipment{vm.doneCount === 1 ? '' : 's'} reconciled today</span>}
          </div>
        </div>

        {/* DC stock */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 16 }}>
          {vm.dcs.map((dc) => {
            const max = Math.max(1, ...dc.rows.map((r) => r.qty_on_hand));
            return (
              <div key={dc.name} style={{ ...card, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={eyebrow}>{dc.name} · on hand</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', columnGap: 20, rowGap: 7 }}>
                  {dc.rows.map((r) => (
                    <div key={r.sku} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 56px', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product_name}</span>
                        <div style={{ height: 3, borderRadius: 2, background: '#1C1C22' }}>
                          <div style={{ width: `${(r.qty_on_hand / max) * 100}%`, height: '100%', borderRadius: 2, background: r.qty_on_hand < 20 ? '#FF6B6B' : '#6F97FF', transition: 'width .5s' }} />
                        </div>
                      </div>
                      <span style={{ font: `500 11.5px ${MONO}`, color: r.qty_on_hand < 20 ? '#FF8A8A' : '#9C9CA6', textAlign: 'right' }}>{n(r.qty_on_hand)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
